import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTenant } from '@/hooks/useTenant';
import { getMainSiteUrl } from '@/lib/domain';
import { resolveStoreName } from '@/lib/display';
import { listPublishedCustomPages } from '@/hooks/useCustomPages';

interface FooterPage {
  slug: string;
  title: string;
}

export default function TenantFooter() {
  const { tenant, theme } = useTenant();
  const [pages, setPages] = useState<FooterPage[]>([]);

  // Les pages personnalisées publiées du marchand apparaissent aussi dans le
  // pied de page du site public (en plus du menu latéral).
  useEffect(() => {
    if (!tenant) return;
    let cancelled = false;
    listPublishedCustomPages(Number(tenant.id))
      .then((list) => {
        if (!cancelled) setPages(list.map((p) => ({ slug: p.slug, title: p.title || p.slug })));
      })
      .catch(() => { if (!cancelled) setPages([]); });
    return () => { cancelled = true; };
  }, [tenant]);

  if (!tenant) return null;

  const storeName = resolveStoreName(tenant, theme);
  const year = new Date().getFullYear();

  const socialLinks = [
    { key: 'facebook', icon: 'ri-facebook-circle-line', url: tenant.lienfacebook, label: 'Facebook' },
    { key: 'instagram', icon: 'ri-instagram-line', url: tenant.lieninstagram, label: 'Instagram' },
    { key: 'tiktok', icon: 'ri-tiktok-line', url: tenant.lientiktok, label: 'TikTok' },
    { key: 'twitter', icon: 'ri-twitter-x-line', url: tenant.lientwitter, label: 'X' },
    { key: 'linkedin', icon: 'ri-linkedin-box-line', url: tenant.lienlinkedin, label: 'LinkedIn' },
    { key: 'youtube', icon: 'ri-youtube-line', url: tenant.lienyoutube, label: 'YouTube' },
  ].filter((s) => /^https?:\/\//i.test((s.url || '').trim()));

  return (
    <footer className="bg-background-100 border-t border-background-200/70 mt-auto">
      <div className="w-full px-4 md:px-6 max-w-7xl mx-auto py-8">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-foreground-800">{storeName}</span>
          </div>
          <div className="flex items-center gap-4 text-xs text-foreground-500">
            {tenant.Pays && <span>{tenant.Ville ? `${tenant.Ville}, ${tenant.Pays}` : tenant.Pays}</span>}
            {tenant.telephone && (
              <a href={`tel:${tenant.telephone}`} className="text-foreground-500 hover:text-foreground-700 transition-colors no-underline">
                <i className="ri-phone-line mr-1"></i>{tenant.telephone}
              </a>
            )}
            {tenant.email && (
              <a href={`mailto:${tenant.email}`} className="text-foreground-500 hover:text-foreground-700 transition-colors no-underline">
                <i className="ri-mail-line mr-1"></i>{tenant.email}
              </a>
            )}
          </div>
        </div>

        {pages.length > 0 && (
          <nav className="mt-6 pt-6 border-t border-background-200/70" aria-label="Pages de la boutique">
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-foreground-400 mb-3 flex items-center gap-1.5">
              <i className="ri-pages-line"></i>
              Nos pages
            </h3>
            <ul className="flex flex-wrap gap-x-6 gap-y-2 list-none p-0 m-0">
              {pages.map((p) => (
                <li key={p.slug}>
                  <Link
                    to={`/p/${p.slug}`}
                    className="text-xs text-foreground-600 hover:text-primary-600 transition-colors no-underline whitespace-nowrap"
                  >
                    {p.title}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        )}

        {socialLinks.length > 0 && (
          <div className="mt-6 pt-6 border-t border-background-200/70">
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-foreground-400 mb-3 flex items-center gap-1.5">
              <i className="ri-links-line"></i>
              Suivez-nous
            </h3>
            <div className="flex flex-wrap gap-2">
              {socialLinks.map((s) => (
                <a
                  key={s.key}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={s.label}
                  aria-label={s.label}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-background-50 border border-background-200/70 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors no-underline"
                >
                  <i className={s.icon}></i>
                </a>
              ))}
            </div>
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-background-200/70 text-center text-xs text-foreground-400">
          &copy; {year} {storeName}. Propulsé par{' '}
          <a href={getMainSiteUrl()} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 font-medium no-underline">
            Zifek
          </a>
        </div>
      </div>
    </footer>
  );
}